<template>
  <v-dialog
    v-model="dialogOpen"
    v-if="dialogOpen"
    persistent
    max-width="800"
    @keydown.esc="close"
  >
    <v-card class="glass-card">
      <v-card-title class="dialog-header">
        <h2 class="dialog-title">{{ mode === 'edit' ? 'Uredi kategoriju ili proizvod' : 'Odaberi kategoriju ili proizvod' }}</h2>
        <v-spacer></v-spacer>
        <v-btn icon class="close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="dialog-content">
        <v-container>
          <v-row>
            <v-col cols="12" md="6">
              <label class="input-label">Kategorija</label>
              <v-select
                v-model="selectedCategory"
                :items="filteredCategories"
                placeholder="Odaberi kategoriju"
                item-text="name"
                item-value="id"
                return-object
                dense
                outlined
                hide-details
                class="filter-select"
                @change="onSelectionChange('category')"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <label class="input-label">Proizvod</label>
              <v-select
                v-model="selectedProduct"
                :items="filteredProducts"
                placeholder="Odaberi proizvod"
                item-text="name"
                item-value="id"
                return-object
                dense
                outlined
                hide-details
                class="filter-select"
                @change="onSelectionChange('product')"
              ></v-select>
            </v-col>
          </v-row>
        <v-row class="mt-2">
          <v-col cols="12" class="d-flex justify-center">
            <div class="preview-section">
              <label class="input-label text-center mb-1">Pregled</label>
              <v-btn
                class="preview-btn"
                :class="{
                  'tile-glass-effect': hasGlassEffect,
                  'tile-3d-effect': has3DEffect,
                  'tile-neon-effect': hasNeonEffect,
                  'tile-pulse-effect': hasPulseEffect,
                  'tile-neumorphism-effect': hasNeumorphismEffect,
                  'tile-gradient-border-effect': hasGradientBorderEffect,
                  'tile-shimmer-effect': hasShimmerEffect,
                  'tile-floating-effect': hasFloatingEffect,
                  'tile-shadow-lift-effect': hasShadowLiftEffect,
                  'tile-holographic-effect': hasHolographicEffect,
                  'tile-frost-effect': hasFrostEffect,
                  'tile-border-glow-effect': hasBorderGlowEffect,
                  'tile-breathing-effect': hasBreathingEffect
                }"
                block
                depressed
                min-height="50px"
                :style="previewStyle"
              >
                <span class="preview-text" :style="`color: ${foregroundColor}`">{{ previewBtnText || 'Odaberi' }}</span>
              </v-btn>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col cols="12" md="4" class="d-flex align-center justify-center">
            <v-checkbox
              v-model="isGradient"
              label="Koristi gradient"
              hide-details
              dense
              class="gradient-checkbox mt-0"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center justify-center">
            <v-checkbox
              v-model="usePreviousStyle"
              label="Koristi prethodni stil"
              hide-details
              dense
              class="gradient-checkbox mt-0"
              @change="onUsePreviousStyleChange"
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col cols="12">
            <v-expansion-panels flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="effects-header">
                  <span class="input-label">
                    <v-icon small class="mr-1">mdi-auto-fix</v-icon>
                    Vizualni efekti
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasGlassEffect"
                        label="Glass effect (staklo)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="has3DEffect"
                        label="3D effect (sjenčanje)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasNeonEffect"
                        label="Neon/Glow effect"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasPulseEffect"
                        label="Pulse effect (pulsiranje)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasNeumorphismEffect"
                        label="Neumorphism (soft UI)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasGradientBorderEffect"
                        label="Gradient border"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasShimmerEffect"
                        label="Shimmer/Sparkle"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasFloatingEffect"
                        label="Floating (lebdenje)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasShadowLiftEffect"
                        label="Shadow lift (izdizanje)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasHolographicEffect"
                        label="Holographic (hologram)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasFrostEffect"
                        label="Frost (led)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasBorderGlowEffect"
                        label="Border glow (rub)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center py-1">
                      <v-checkbox
                        v-model="hasBreathingEffect"
                        label="Breathing (disanje)"
                        hide-details
                        dense
                        class="gradient-checkbox mt-0"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col cols="12" :md="isGradient ? 4 : 6">
            <label class="input-label">{{ isGradient ? 'Boja 1' : 'Boja pozadine' }}</label>
            <v-color-picker
              show-swatches
              :swatches="swatches"
              canvas-height="80"
              mode="hexa"
              v-model="backgroundColor"
              class="color-picker"
            ></v-color-picker>
          </v-col>
          <v-col cols="12" md="4" v-if="isGradient">
            <label class="input-label">Boja 2</label>
            <v-color-picker
              show-swatches
              :swatches="swatches"
              canvas-height="80"
              mode="hexa"
              v-model="backgroundColorSecondary"
              class="color-picker"
            ></v-color-picker>
          </v-col>
          <v-col cols="12" :md="isGradient ? 4 : 6">
            <label class="input-label">Boja teksta</label>
            <v-color-picker
              show-swatches
              :swatches="swatches"
              canvas-height="80"
              mode="hexa"
              v-model="foregroundColor"
              class="color-picker"
            ></v-color-picker>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions class="dialog-actions">
      <v-btn
        class="action-btn action-btn-primary"
        @click="onAddItem('category')"
        :disabled="!categoryIsSelected"
        small
      >
        <v-icon small class="mr-1">mdi-folder</v-icon>
        {{ mode === 'edit' ? 'Spremi kategoriju' : 'Dodaj kategoriju' }}
      </v-btn>
      <v-btn
        class="action-btn action-btn-primary"
        @click="onAddItem('product')"
        :disabled="!productIsSelected"
        small
      >
        <v-icon small class="mr-1">mdi-package-variant</v-icon>
        {{ mode === 'edit' ? 'Spremi proizvod' : 'Dodaj proizvod' }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        v-if="mode === 'edit'"
        class="action-btn action-btn-danger"
        @click="onDeleteItem"
        small
      >
        <v-icon small class="mr-1">mdi-delete</v-icon>
        Ukloni
      </v-btn>
      <v-btn
        class="action-btn action-btn-secondary"
        @click="close"
        small
      >
        Odustani
      </v-btn>
    </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import state from '@/state'

export default {
  data () {
    return {
      dialogOpen: false,
      products: [],
      currency: 'EUR',
      selectedCategory: undefined,
      selectedProduct: undefined,
      categoryIsSelected: false,
      productIsSelected: false,
      colspan: 1,
      backgroundColor: {},
      backgroundColorSecondary: {},
      foregroundColor: {},
      isGradient: false,
      usePreviousStyle: false,
      hasGlassEffect: false,
      has3DEffect: false,
      hasNeonEffect: false,
      hasPulseEffect: false,
      hasNeumorphismEffect: false,
      hasGradientBorderEffect: false,
      hasShimmerEffect: false,
      hasFloatingEffect: false,
      hasShadowLiftEffect: false,
      hasHolographicEffect: false,
      hasFrostEffect: false,
      hasBorderGlowEffect: false,
      hasBreathingEffect: false,
      swatches: [
        ['#E8DFD0', '#C7B8A1', '#A69580', '#8B7B68', '#6B5D50'],
        ['#D4E4F7', '#B8D4F1', '#9BBFE0', '#7FA5C8', '#6690B8'],
        ['#C8E6D3', '#A8D5BA', '#88C4A0', '#6BAF8A', '#539975'],
        ['#F5D6E3', '#EDB8CF', '#E39AB9', '#D57CA3', '#C4608E'],
        ['#FFE5CC', '#FFD1A3', '#FFBD7A', '#FFA952', '#FF9529'],
        ['#E8D7F1', '#D5BFE8', '#C1A7DE', '#AD8FD3', '#9977C8']
      ],
      previewBtnText: ''
    }
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'add', // 'add' or 'edit'
      validator: (value) => ['add', 'edit'].includes(value)
    },
    editItem: {
      type: Object,
      default: () => ({})
    },
    categories: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    filteredCategories () {
      const cats = this.categories.filter(cat => cat.status === 'OK')
      return clone(cats.sort((a, b) => a.name.localeCompare(b.name)))
    },
    filteredProducts () {
      const prods = this.products.filter(prod => prod.status === 'OK')
      return clone(prods.sort((a, b) => a.name.localeCompare(b.name)))
    },
    previewStyle () {
      if (this.isGradient) {
        return `background: linear-gradient(135deg, ${this.backgroundColor} 0%, ${this.backgroundColorSecondary} 100%) !important;`
      }
      return `background: ${this.backgroundColor} !important;`
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      async handler (newValue) {
        if (!newValue) {
          this.dialogOpen = false
          return
        }

        this.dialogOpen = newValue

        if (this.mode === 'add') {
          this.reset()
        } else if (this.mode === 'edit' && this.editItem) {
          // Initialize from editItem for edit mode
          await this.initializeEditMode()
        }
      }
    },
    color (newValue) {
    }
  },
  mounted () {
    this.currency = 'HRK'
    if (Date.now() / 1000 > 1672527599) {
      this.currency = 'EUR'
    }
    this.backgroundColor = '#B8D4F1'
    this.backgroundColorSecondary = '#9BBFE0'
    this.foregroundColor = '#2C3E50'
  },
  methods: {
    close () {
      if (this.mode === 'add') {
        this.$emit('addItemDialogClose')
      } else {
        this.$emit('editItemDialogClose')
      }
    },
    async initializeEditMode () {
      // Check if the color is a gradient
      if (this.editItem.color && this.editItem.color.includes('linear-gradient')) {
        this.isGradient = true
        // Extract colors from gradient string
        const gradientMatch = this.editItem.color.match(/#[0-9A-Fa-f]{6}/g)
        if (gradientMatch && gradientMatch.length >= 2) {
          this.backgroundColor = gradientMatch[0]
          this.backgroundColorSecondary = gradientMatch[1]
        } else {
          // Fallback to default colors
          this.backgroundColor = '#B8D4F1'
          this.backgroundColorSecondary = '#9BBFE0'
        }
      } else {
        this.isGradient = false
        this.backgroundColor = this.editItem.color || '#B8D4F1'
        this.backgroundColorSecondary = '#9BBFE0'
      }
      this.foregroundColor = this.editItem.foregroundColor || '#2C3E50'
      this.previewBtnText = this.editItem.label || ''
      this.colspan = this.editItem.colspan || 1

      // Initialize visual effects from editItem
      this.hasGlassEffect = this.editItem.hasGlassEffect || false
      this.has3DEffect = this.editItem.has3DEffect || false
      this.hasNeonEffect = this.editItem.hasNeonEffect || false
      this.hasPulseEffect = this.editItem.hasPulseEffect || false
      this.hasNeumorphismEffect = this.editItem.hasNeumorphismEffect || false
      this.hasGradientBorderEffect = this.editItem.hasGradientBorderEffect || false
      this.hasShimmerEffect = this.editItem.hasShimmerEffect || false
      this.hasFloatingEffect = this.editItem.hasFloatingEffect || false
      this.hasShadowLiftEffect = this.editItem.hasShadowLiftEffect || false
      this.hasHolographicEffect = this.editItem.hasHolographicEffect || false
      this.hasFrostEffect = this.editItem.hasFrostEffect || false
      this.hasBorderGlowEffect = this.editItem.hasBorderGlowEffect || false
      this.hasBreathingEffect = this.editItem.hasBreathingEffect || false

      // Initialize selectedCategory or selectedProduct from editItem
      if (this.editItem.type === 'category') {
        this.selectedCategory = this.filteredCategories.find(cat => cat.id === this.editItem.id) || {
          id: this.editItem.id,
          name: this.editItem.label
        }
        this.categoryIsSelected = true
        this.productIsSelected = false
        // Load products for this category
        if (this.editItem.id) {
          await this.getProductsByCategory(this.editItem.id)
        }
      } else if (this.editItem.type === 'product' || this.editItem.type === 'menu-product') {
        // First, find the category from filteredCategories using parent_id
        const parentId = this.editItem.parent_id || this.editItem.parentId
        this.selectedCategory = this.filteredCategories.find(cat => cat.id === parentId) || {
          id: parentId,
          name: ''
        }
        this.categoryIsSelected = true

        // Load products for this category and wait for completion
        if (parentId) {
          await this.getProductsByCategory(parentId)

          // After products are loaded, set the selected product
          this.$nextTick(() => {
            this.selectedProduct = this.filteredProducts.find(prod => prod.id === this.editItem.id) || {
              id: this.editItem.id,
              name: this.editItem.label
            }
            this.productIsSelected = true
          })
        }
      }
    },
    onSelectionChange (type) {
      if (type === 'category') {
        this.categoryIsSelected = true
        this.productIsSelected = false
        this.selectedProduct = undefined
        this.previewBtnText = this.selectedCategory.name
        this.getProductsByCategory(this.selectedCategory.id)
      } else {
        // When product is selected, enable both buttons (product and category)
        this.productIsSelected = true
        this.categoryIsSelected = true
        this.previewBtnText = this.selectedProduct.name
      }
    },
    onAddItem (type) {
      // Create background style (solid color or gradient)
      const backgroundStyle = this.isGradient
        ? `linear-gradient(135deg, ${this.backgroundColor} 0%, ${this.backgroundColorSecondary} 100%)`
        : this.backgroundColor

      // Save current style to localStorage
      this.savePreviousStyle()

      const effects = {
        hasGlassEffect: this.hasGlassEffect,
        has3DEffect: this.has3DEffect,
        hasNeonEffect: this.hasNeonEffect,
        hasPulseEffect: this.hasPulseEffect,
        hasNeumorphismEffect: this.hasNeumorphismEffect,
        hasGradientBorderEffect: this.hasGradientBorderEffect,
        hasShimmerEffect: this.hasShimmerEffect,
        hasFloatingEffect: this.hasFloatingEffect,
        hasShadowLiftEffect: this.hasShadowLiftEffect,
        hasHolographicEffect: this.hasHolographicEffect,
        hasFrostEffect: this.hasFrostEffect,
        hasBorderGlowEffect: this.hasBorderGlowEffect,
        hasBreathingEffect: this.hasBreathingEffect
      }

      if (this.mode === 'add') {
        // Add mode - emit add events
        if (type === 'category') {
          this.$emit('addCategoryToPanel', this.selectedCategory, this.colspan, backgroundStyle, this.foregroundColor, effects)
        } else {
          this.$emit('addProductToPanel', this.selectedProduct, this.selectedCategory, this.colspan, backgroundStyle, this.foregroundColor, effects)
        }
        this.reset()
        this.$emit('addItemDialogClose')
      } else {
        // Edit mode - emit change events
        const updatedItem = {
          active: true,
          col_index: this.editItem.col_index,
          row_index: this.editItem.row_index,
          color: backgroundStyle,
          foregroundColor: this.foregroundColor,
          colspan: this.colspan,
          id: type === 'category' ? this.selectedCategory.id : this.selectedProduct.id,
          label: type === 'category' ? this.selectedCategory.name : this.selectedProduct.name,
          type: type === 'category' ? 'category' : (this.selectedProduct.products ? 'menu-product' : 'product'),
          parent_id: type === 'category' ? '' : this.selectedCategory.id,
          ...effects
        }

        if (type === 'category') {
          this.$emit('changeCategoryOnPanel', updatedItem, this.colspan, backgroundStyle, this.foregroundColor, effects)
        } else {
          this.$emit('changeProductOnPanel', updatedItem, this.colspan, backgroundStyle, this.foregroundColor, effects)
        }
        this.$emit('editItemDialogClose')
      }
    },
    onDeleteItem () {
      this.$emit('deleteItem', this.editItem)
      this.$emit('editItemDialogClose')
    },
    savePreviousStyle () {
      const styleData = {
        backgroundColor: this.backgroundColor,
        backgroundColorSecondary: this.backgroundColorSecondary,
        foregroundColor: this.foregroundColor,
        isGradient: this.isGradient,
        hasGlassEffect: this.hasGlassEffect,
        has3DEffect: this.has3DEffect,
        hasNeonEffect: this.hasNeonEffect,
        hasPulseEffect: this.hasPulseEffect,
        hasNeumorphismEffect: this.hasNeumorphismEffect,
        hasGradientBorderEffect: this.hasGradientBorderEffect,
        hasShimmerEffect: this.hasShimmerEffect,
        hasFloatingEffect: this.hasFloatingEffect,
        hasShadowLiftEffect: this.hasShadowLiftEffect,
        hasHolographicEffect: this.hasHolographicEffect,
        hasFrostEffect: this.hasFrostEffect,
        hasBorderGlowEffect: this.hasBorderGlowEffect,
        hasBreathingEffect: this.hasBreathingEffect
      }
      localStorage.setItem('tileStylePreset', JSON.stringify(styleData))
    },
    loadPreviousStyle () {
      const savedStyle = localStorage.getItem('tileStylePreset')
      if (savedStyle) {
        try {
          const styleData = JSON.parse(savedStyle)
          this.backgroundColor = styleData.backgroundColor || '#B8D4F1'
          this.backgroundColorSecondary = styleData.backgroundColorSecondary || '#9BBFE0'
          this.foregroundColor = styleData.foregroundColor || '#2C3E50'
          this.isGradient = styleData.isGradient || false
          this.hasGlassEffect = styleData.hasGlassEffect || false
          this.has3DEffect = styleData.has3DEffect || false
          this.hasNeonEffect = styleData.hasNeonEffect || false
          this.hasPulseEffect = styleData.hasPulseEffect || false
          this.hasNeumorphismEffect = styleData.hasNeumorphismEffect || false
          this.hasGradientBorderEffect = styleData.hasGradientBorderEffect || false
          this.hasShimmerEffect = styleData.hasShimmerEffect || false
          this.hasFloatingEffect = styleData.hasFloatingEffect || false
          this.hasShadowLiftEffect = styleData.hasShadowLiftEffect || false
          this.hasHolographicEffect = styleData.hasHolographicEffect || false
          this.hasFrostEffect = styleData.hasFrostEffect || false
          this.hasBorderGlowEffect = styleData.hasBorderGlowEffect || false
          this.hasBreathingEffect = styleData.hasBreathingEffect || false
        } catch (e) {
          console.error('Error loading previous style:', e)
        }
      }
    },
    onUsePreviousStyleChange (value) {
      if (value) {
        this.loadPreviousStyle()
      }
    },
    reset () {
      this.selectedCategory = {}
      this.selectedProduct = {}
      this.categoryIsSelected = false
      this.productIsSelected = false
    },
    async getProductsByCategory (categoryId) {
      if (categoryId === '5692439e-4269-4fd9-875e-71e0e08a4e99') {
        const query = await df.collection(`location_menu_pricelist/${state.getPointOfSale().id}/items`)
          .where('status', '==', 'OK')
          .where('active', '==', true)
          // .where(`prices.${this.currency}.active`, '==', true)
          .get()

        const documents = query.docs.map(doc => doc.data())
        this.products = [...documents]
      } else {
        const query = await df.collection(`location_pricelist/${state.getPointOfSale().id}/items`)
          .where('category_ids', 'array-contains', `${categoryId}`)
          .where('status', '==', 'OK')
          .where('active', '==', true)
          // .where(`prices.${this.currency}.active`, '==', true)
          .get()

        this.products = []
        const documents = query.docs.map(doc => doc.data())
        documents.forEach(document => {
          if (document.warehouse_units && document.warehouse_units.units) {
            Object.keys(document.warehouse_units.units).forEach(key => {
              if (document.warehouse_units.units[key].prices && document.warehouse_units.units[key].prices[this.currency].active === true) {
                let newProduct = {}
                newProduct = clone(document)
                newProduct.prices = document.warehouse_units.units[key].prices
                // if (document.warehouse_units.units[key].storage_unit && document.warehouse_units.units[key].storage_unit !== '') {
                //   newProduct.name = document.warehouse_units.units[key].storage_unit
                // } else {
                //   newProduct.name = document.name
                // }

                var stUnit = document.warehouse_units.units[key].storage_unit
                if (stUnit === '.kom') {
                  stUnit = ''
                }
                if (document.warehouse_units.units[key].base_unit === true) {
                  stUnit = document.selling_unit
                }
                newProduct.name = document.name + ' ' + stUnit
                newProduct.id = newProduct.id + '.' + document.warehouse_units.units[key].id
                this.products.push(newProduct)
              }
            })
          } else {
            this.products.push(document)
          }
        })
      }
    }
  }
}
</script>

<style scoped>
/* Glass Card */
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.15) !important;
  overflow: hidden;
}

/* Dialog Header with Gradient */
.dialog-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  padding: 16px 20px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.dialog-title {
  font-size: 22px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 0.95) 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
  letter-spacing: 0.5px !important;
  display: inline-block !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.close-btn {
  color: white !important;
  transition: all 0.2s ease !important;
}

.close-btn:hover {
  transform: scale(1.1) rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Dialog Content */
.dialog-content {
  padding: 16px 20px !important;
  background: rgba(255, 255, 255, 0.98) !important;
}

/* Input Labels */
.input-label {
  display: block;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: rgba(57, 137, 201, 0.9) !important;
  margin-bottom: 6px !important;
  letter-spacing: 0.3px !important;
}

/* Filter Select Inputs (Dropdowns) */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 40px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
}

/* Filter Select Dropdown Menu */
.filter-select >>> .v-menu__content {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 8px 24px rgba(0, 179, 190, 0.12) !important;
}

.filter-select >>> .v-list-item {
  font-size: 14px !important;
  transition: all 0.2s ease !important;
}

.filter-select >>> .v-list-item:hover {
  background: rgba(57, 137, 201, 0.08) !important;
}

.filter-select >>> .v-list-item--active {
  background: rgba(0, 179, 190, 0.12) !important;
  color: #3989C9 !important;
}

/* Preview Section */
.preview-section {
  width: 300px;
  max-width: 100%;
}

.preview-btn {
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.preview-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
}

.preview-text {
  font-weight: 700 !important;
  font-size: 15px !important;
  text-align: center;
  word-break: break-word;
}

/* Color Picker Styling */
.color-picker {
  border-radius: 8px !important;
  overflow: hidden;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 2px 8px rgba(0, 179, 190, 0.1) !important;
}

/* Gradient Checkbox */
.gradient-checkbox >>> .v-label {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.9) !important;
}

.gradient-checkbox >>> .v-input--selection-controls__input {
  color: #3989C9 !important;
}

/* Expansion Panel for Effects */
.effects-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.05) 0%,
    rgba(57, 137, 201, 0.03) 100%) !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  min-height: 48px !important;
}

.effects-header .input-label {
  display: flex;
  align-items: center;
  font-weight: 600 !important;
}

.v-expansion-panels {
  border-radius: 8px !important;
}

.v-expansion-panel {
  border-radius: 8px !important;
}

.v-expansion-panel::before {
  box-shadow: none !important;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 16px 8px 8px 8px !important;
}

/* Dialog Actions */
.dialog-actions {
  background: rgba(248, 249, 250, 0.95) !important;
  padding: 12px 20px !important;
  border-top: 1px solid rgba(0, 179, 190, 0.15) !important;
}

/* Action Buttons */
.action-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  height: 36px !important;
  transition: all 0.2s ease !important;
}

.action-btn-primary {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 2px 8px rgba(0, 179, 190, 0.3) !important;
  margin-right: 8px !important;
}

.action-btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 0.95) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 179, 190, 0.4) !important;
}

.action-btn-primary:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

.action-btn-primary .v-icon {
  color: white !important;
}

.action-btn-secondary {
  background: rgba(255, 255, 255, 0.95) !important;
  color: rgba(70, 70, 80, 0.9) !important;
  border: 1px solid rgba(200, 200, 200, 0.4) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
}

.action-btn-secondary:hover {
  background: rgba(248, 249, 250, 1) !important;
  border-color: rgba(150, 150, 150, 0.5) !important;
  transform: translateY(-1px);
}

.action-btn-danger {
  background: linear-gradient(135deg,
    rgba(244, 67, 54, 0.95) 0%,
    rgba(229, 57, 53, 0.95) 100%) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3) !important;
}

.action-btn-danger:hover {
  background: linear-gradient(135deg,
    rgba(244, 67, 54, 1) 0%,
    rgba(229, 57, 53, 1) 100%) !important;
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.4) !important;
}

.action-btn-danger .v-icon {
  color: white !important;
}

/* Visual Effects for Preview */

/* Glass Effect */
.tile-glass-effect {
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  position: relative;
  overflow: hidden;
}

.tile-glass-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  pointer-events: none;
  z-index: 1;
}

.tile-glass-effect::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: glass-shine 3s infinite;
  pointer-events: none;
  z-index: 2;
}

@keyframes glass-shine {
  0% {
    left: -100%;
  }
  50%, 100% {
    left: 100%;
  }
}

/* 3D Effect */
.tile-3d-effect {
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 4px 8px rgba(0, 0, 0, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.08),
    inset 0 -2px 4px rgba(0, 0, 0, 0.1),
    inset 0 2px 4px rgba(255, 255, 255, 0.3) !important;
  transform: translateZ(0);
  position: relative;
}

.tile-3d-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 30%;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  border-radius: 12px 12px 0 0;
  pointer-events: none;
  z-index: 1;
}

/* Neon/Glow Effect */
.tile-neon-effect {
  box-shadow:
    0 0 5px currentColor,
    0 0 10px currentColor,
    0 0 20px currentColor,
    0 0 40px currentColor !important;
  border: 2px solid rgba(255, 255, 255, 0.5) !important;
  filter: brightness(1.1);
}

/* Pulse Effect */
.tile-pulse-effect {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.95;
  }
}

/* Neumorphism Effect */
.tile-neumorphism-effect {
  box-shadow:
    8px 8px 16px rgba(0, 0, 0, 0.15),
    -8px -8px 16px rgba(255, 255, 255, 0.7) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

/* Gradient Border Effect */
.tile-gradient-border-effect {
  position: relative;
  border: 3px solid transparent !important;
  background-clip: padding-box;
}

.tile-gradient-border-effect::before {
  content: '';
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  background: linear-gradient(
    45deg,
    #ff6b6b,
    #4ecdc4,
    #45b7d1,
    #f7dc6f,
    #ff6b6b
  );
  border-radius: 12px;
  z-index: -1;
  animation: gradient-rotate 4s linear infinite;
  background-size: 300% 300%;
}

@keyframes gradient-rotate {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* Shimmer/Sparkle Effect */
.tile-shimmer-effect {
  position: relative;
  overflow: hidden;
}

.tile-shimmer-effect::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.4) 20%,
    transparent 40%
  );
  animation: shimmer 3s infinite;
  pointer-events: none;
  z-index: 10;
}

@keyframes shimmer {
  0% {
    transform: translate(-100%, -100%) rotate(0deg);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translate(100%, 100%) rotate(360deg);
    opacity: 0;
  }
}

/* Floating Effect */
.tile-floating-effect {
  animation: floating 3s ease-in-out infinite;
}

@keyframes floating {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

/* Shadow Lift Effect */
.tile-shadow-lift-effect {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25) !important;
  transform: translateY(-4px);
  transition: all 0.3s ease;
}

.tile-shadow-lift-effect:hover {
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
  transform: translateY(-6px);
}

/* Holographic Effect */
.tile-holographic-effect {
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    45deg,
    #ff0080 0%,
    #ff8c00 15%,
    #40e0d0 30%,
    #00ff00 45%,
    #4169e1 60%,
    #ff0080 75%,
    #ff8c00 100%
  ) !important;
  background-size: 200% 200%;
  animation: holographic 4s linear infinite;
}

.tile-holographic-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.4) 0%,
    transparent 50%,
    rgba(255, 255, 255, 0.3) 100%
  );
  animation: holographic-shimmer 2s ease-in-out infinite;
  pointer-events: none;
}

@keyframes holographic {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes holographic-shimmer {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

/* Frost/Ice Effect */
.tile-frost-effect {
  position: relative;
  backdrop-filter: blur(8px) brightness(1.1) !important;
  -webkit-backdrop-filter: blur(8px) brightness(1.1) !important;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(230, 247, 255, 0.4) 50%,
    rgba(200, 235, 255, 0.5) 100%
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
  box-shadow:
    inset 0 1px 2px rgba(255, 255, 255, 0.8),
    inset 0 -1px 2px rgba(0, 0, 0, 0.1),
    0 4px 12px rgba(0, 100, 255, 0.15) !important;
}

.tile-frost-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 70%
  );
  background-size: 200% 200%;
  animation: frost-sparkle 3s ease-in-out infinite;
  pointer-events: none;
}

@keyframes frost-sparkle {
  0%, 100% {
    background-position: 0% 0%;
    opacity: 0.5;
  }
  50% {
    background-position: 100% 100%;
    opacity: 0.8;
  }
}

/* Border Glow Effect */
.tile-border-glow-effect {
  border: 2px solid currentColor !important;
  box-shadow:
    0 0 8px currentColor,
    0 0 16px currentColor,
    inset 0 0 8px currentColor !important;
  animation: border-glow-pulse 2s ease-in-out infinite;
}

@keyframes border-glow-pulse {
  0%, 100% {
    box-shadow:
      0 0 8px currentColor,
      0 0 16px currentColor,
      inset 0 0 8px currentColor;
  }
  50% {
    box-shadow:
      0 0 12px currentColor,
      0 0 24px currentColor,
      inset 0 0 12px currentColor;
  }
}

/* Breathing Effect */
.tile-breathing-effect {
  animation: breathing 4s ease-in-out infinite;
}

@keyframes breathing {
  0%, 100% {
    opacity: 0.85;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.01);
  }
}
</style>
